// PreventiApp · Sprint 3 · Etapa 7 — AdminLayout con sidebar rol-aware.
import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Building2, Users, BookOpen, ClipboardList, LifeBuoy, Upload,
  Home, LogOut, Smartphone, ShieldCheck, Menu, X, LayoutDashboard,
} from 'lucide-react';

// permisos derivados de profile
function buildNav(profile) {
  const rol = profile?.rol;
  const perms = profile?.permisos_inside || {};
  const isInside = profile?.es_inside_staff === true;
  const isAdminColegio = rol === 'admin_colegio';
  const verObs = ['admin_colegio','encargado_convivencia','directivo','orientador','docente','inspector'].includes(rol);
  const verDashboard = ['super_admin_inside','admin_colegio','directivo','encargado_convivencia','orientador'].includes(rol);
  const items = [
    { to: '/admin', label: 'Inicio', icon: Home, exact: true },
  ];
  if (verDashboard) {
    items.push({ to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard });
  }
  if (isInside && (rol === 'super_admin_inside' || perms.puede_gestionar_colegios)) {
    items.push({ to: '/admin/colegios', label: 'Colegios', icon: Building2 });
  }
  if (isInside || isAdminColegio) {
    items.push({ to: '/admin/usuarios', label: 'Usuarios', icon: Users });
  }
  if (isAdminColegio) {
    items.push({ to: '/admin/cursos', label: 'Cursos', icon: BookOpen });
  }
  if (verObs || isInside) {
    items.push({ to: '/admin/observaciones', label: 'Observaciones', icon: ClipboardList });
  }
  items.push({ to: '/admin/soporte', label: 'Soporte INSIDE', icon: LifeBuoy });
  if (isInside && (rol === 'super_admin_inside' || perms.puede_gestionar_usuarios)) {
    items.push({ to: '/admin/import', label: 'Carga masiva', icon: Upload });
  }
  return items;
}

export default function AdminLayout() {
  const { profile, session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = buildNav(profile);
  const isDocente = ['docente','inspector'].includes(profile?.rol);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cerrar el drawer al cambiar de ruta (evita quedar abierto tras navegar)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#F4F7FE] relative" data-testid="admin-layout">
      {/* Top bar sólo en móvil */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E5EAF5] px-4 py-3 flex items-center justify-between" data-testid="admin-topbar-mobile">
        <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu-btn" aria-label="Abrir menú" className="p-2 rounded-lg text-[#0B1B5E] hover:bg-[#F4F7FE]">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#5346E6]" />
          <span className="text-sm font-extrabold text-[#0B1B5E]">PreventiApp</span>
        </div>
        <button onClick={signOut} data-testid="admin-mobile-logout" aria-label="Cerrar sesión" className="p-2 rounded-lg text-[#0B1B5E] hover:bg-[#F4F7FE]">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Overlay cuando el drawer está abierto en móvil */}
      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Cerrar menú"
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          data-testid="admin-mobile-overlay"
        />
      )}

      {/* Sidebar: siempre visible en desktop; drawer en móvil */}
      <aside
        className={`w-60 shrink-0 bg-white border-r border-[#E5EAF5] flex flex-col z-50 transition-transform
          lg:relative lg:translate-x-0
          fixed inset-y-0 left-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-5 border-b border-[#E5EAF5] flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-[#5346E6]" />
            <span className="text-base font-extrabold text-[#0B1B5E]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              PreventiApp
            </span>
          </Link>
          {/* Close button en móvil */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1 rounded text-[#475472] hover:text-[#0B1B5E]"
            data-testid="admin-mobile-close-btn"
            aria-label="Cerrar menú"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto" data-testid="admin-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={!!it.exact}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition ${isActive ? 'bg-[#EEF0FE] text-[#5346E6] font-bold border-r-4 border-[#5346E6]' : 'text-[#475472] hover:bg-[#F4F7FE]'}`
              }
              data-testid={`nav-${it.to.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              <it.icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          ))}
          {isDocente && (
            <button
              onClick={() => navigate('/')}
              className="mt-2 flex items-center gap-3 w-full px-5 py-2.5 text-sm text-[#1E9F73] hover:bg-[#DFF6EC] font-bold"
              data-testid="nav-app-movil"
            >
              <Smartphone className="w-4 h-4" />
              Ir a la app móvil
            </button>
          )}
        </nav>

        <div className="border-t border-[#E5EAF5] p-4 text-xs">
          <p className="font-bold text-[#0B1B5E] truncate" data-testid="sidebar-user-name">
            {profile?.nombre_completo || session?.user?.email || '—'}
          </p>
          <p className="text-[#475472] truncate" data-testid="sidebar-user-rol">{profile?.rol || '—'}</p>
          {profile?.es_inside_staff && (
            <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-[#EEF0FE] text-[#5346E6] font-bold text-[10px]">INSIDE</span>
          )}
          <button
            onClick={signOut}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-[#0B1B5E] hover:bg-[#5346E6] text-white text-xs font-bold"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
